import { useState } from "react";
import { Link } from "wouter";
import { useCoiLinks, useCoiLinkMutations } from "@/hooks/useCoiLinks";
import { LinkedCompaniesAddPopover } from "./LinkedCompaniesAddPopover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Edit, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_ROUTE: Record<string, string> = {
  "Client": "/clients",
  "Buyer Network": "/buyers",
  "Prospects": "/prospects",
  "COI": "/companies",
  "Capital Providers": "/capital-providers",
};

interface Props { coiId: string }

export function CoiLinkedCompanies({ coiId }: Props) {
  const links = useCoiLinks(coiId);
  const { add, updateNote, remove } = useCoiLinkMutations(coiId);
  const [editing, setEditing] = useState<{ id: string; note: string } | null>(null);

  return (
    <div className="border rounded-lg p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Linked Companies</h3>
        <LinkedCompaniesAddPopover
          busy={add.isPending}
          onAdd={(i) => add.mutate(i, { onSuccess: () => toast.success("Linked"), onError: (e) => toast.error(String(e)) })}
        />
      </div>
      {!links.data || links.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">No linked companies yet. Click + Add to link a company.</p>
      ) : (
        <ul className="divide-y">
          {links.data.map((l) => {
            const route = CATEGORY_ROUTE[l.companyCategory ?? ""] ?? "/clients";
            const isEditing = editing?.id === l.id;
            return (
              <li key={l.id} className="py-2 flex items-start justify-between gap-2 group">
                <div className="min-w-0 flex-1">
                  <Link href={`${route}/${l.companyId}`} className="font-medium text-sm hover:underline">{l.companyName}</Link>
                  {isEditing ? (
                    <div className="flex items-center gap-1 mt-1">
                      <Input value={editing.note} onChange={(e) => setEditing({ id: l.id, note: e.target.value })}
                        className="h-7 text-xs" autoFocus />
                      <Button size="icon" variant="ghost" className="h-7 w-7"
                        onClick={() => updateNote.mutate({ linkId: l.id, note: editing.note }, { onSuccess: () => { setEditing(null); toast.success("Updated"); } })}>
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(null)}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {l.note || <span className="italic opacity-50">No note</span>}
                    </p>
                  )}
                </div>
                {!isEditing && (
                  <div className="opacity-0 group-hover:opacity-100 transition flex items-center gap-1">
                    <button onClick={() => setEditing({ id: l.id, note: l.note ?? "" })} className="p-1 hover:bg-muted rounded">
                      <Edit className="w-3 h-3 text-muted-foreground" />
                    </button>
                    <button
                      onClick={() => remove.mutate(l.id, { onSuccess: () => toast.success("Removed") })}
                      className="p-1 hover:bg-muted rounded">
                      <Trash2 className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
